import { useCallback, useState } from 'react'

/**
 * Uma preferência que vive **no dispositivo**, não na casa.
 *
 * `Docs/04-Design/Telas/Dashboard.md` pede isso para o olho que esconde o saldo:
 * *"essa preferência persiste no dispositivo"*. É a escolha certa para ela — o
 * motivo de esconder o saldo é quem está olhando a tela, e sincronizar isso
 * entre os dois celulares da casa esconderia o saldo de quem não pediu.
 *
 * ## Todo acesso é embrulhado, e isso não é zelo
 *
 * `localStorage` **lança** — não devolve `null` — em Safari no modo privado com
 * a cota estourada, e em navegador com cookies de terceiros bloqueados dentro de
 * um iframe. Uma exceção no primeiro render derruba a tela inteira, e o valor em
 * jogo é se um número está borrado. O `catch` devolve o padrão.
 *
 * ## A leitura é um inicializador PREGUIÇOSO, não um efeito
 *
 * `useState(() => ler(key) ?? padrao)` roda uma vez, antes do primeiro quadro —
 * então **não há piscada**: quem escolheu esconder o saldo nunca o vê aparecer.
 * A alternativa, ler num `useEffect`, tem dois problemas: mostra o padrão por um
 * quadro (e o quadro em questão exibiria o número que a pessoa mandou esconder),
 * e é reprovada por `react-hooks/set-state-in-effect` — a mesma regra que
 * `use-monthly-book.ts` e `use-members.ts` já contornaram.
 *
 * Ler `localStorage` durante a renderização seria um problema com SSR, por causa
 * da hidratação. Este app é **SPA pura** (ADR-0001): não há servidor
 * renderizando, e não há o que casar.
 */

const PREFIXO = 'porquinho:'

function ler(key: string): string | null {
  try {
    return localStorage.getItem(PREFIXO + key)
  } catch {
    return null
  }
}

function escrever(key: string, value: string): void {
  try {
    localStorage.setItem(PREFIXO + key, value)
  } catch {
    // Sem armazenamento, a preferência vale só para esta sessão. É melhor que
    // derrubar a tela — e não há nada a dizer a quem usa: o app funciona.
  }
}

/** Um booleano guardado no dispositivo. `padrao` vale quando nada foi guardado. */
export function useDeviceFlag(key: string, padrao: boolean): [boolean, (value: boolean) => void] {
  const [value, setValue] = useState(() => {
    const guardado = ler(key)
    // Só `'true'` e `'false'` contam. Qualquer outra coisa guardada — de uma
    // versão anterior, ou de alguém mexendo no console — cai no padrão em vez
    // de virar `true` por ser uma string não vazia.
    return guardado === 'true' || guardado === 'false' ? guardado === 'true' : padrao
  })

  const set = useCallback(
    (next: boolean) => {
      setValue(next)
      escrever(key, String(next))
    },
    [key],
  )

  return [value, set]
}
